"use client";

import { useState } from "react";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "react-phone-number-input";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import ProgressButton from "@/components/ui/Button/ProgressButton";
import MeetingPreferenceSelector from "./MeetingPreferenceSelector";

function GuestBookingFormInner({
    mentor,
    selectedSlot,
    selectedTimezone,
    prospectionToken,
    onBack,
    onBooked,
}) {
    const t = useTranslations();
    const locale = useLocale();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [meetingPreference, setMeetingPreference] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const mentorName = `${mentor.firstName || ""} ${
        mentor.lastName || ""
    }`.trim();

    // Tous les champs requis avant d'activer le bouton de confirmation.
    const isFormValid =
        firstName.trim() &&
        lastName.trim() &&
        email.trim() &&
        subject.trim() &&
        meetingPreference &&
        (meetingPreference !== "phone" ||
            isValidPhoneNumber(phoneNumber || ""));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            toast.error(t("GuestBooking_missing_fields"));
            return;
        }
        if (!meetingPreference) {
            toast.error(t("GuestBooking_preference_required"));
            return;
        }
        if (
            meetingPreference === "phone" &&
            !isValidPhoneNumber(phoneNumber || "")
        ) {
            toast.error(t("GuestBooking_phone_required"));
            return;
        }
        if (!executeRecaptcha) {
            toast.error(t("GuestBooking_error"));
            return;
        }

        setSubmitting(true);
        try {
            const recaptchaToken = await executeRecaptcha("guest_booking");
            const response = await fetch("/api/call/post/guest-call", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    mentorPseudo: mentor.mentorPseudo,
                    date: selectedSlot.start.utc().format("YYYY-MM-DD"),
                    hour: selectedSlot.start.utc().format("HH:mm"),
                    mentorTimezone: mentor.timezone,
                    menteeTimezone: selectedTimezone,
                    guestEmail: email.trim(),
                    guestFirstName: firstName.trim(),
                    guestLastName: lastName.trim(),
                    subject: subject.trim(),
                    meetingPreference,
                    guestPhone:
                        meetingPreference === "phone"
                            ? phoneNumber.trim()
                            : "",
                    prospectionToken,
                    recaptchaToken,
                    locale,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setSubmitting(false);
                toast.error(t("GuestBooking_error"));
                return;
            }

            onBooked();
        } catch {
            setSubmitting(false);
            toast.error(t("GuestBooking_error"));
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:brightness-90 transition mb-5"
            >
                <SolarIcon name="alt-arrow-left-linear" className="text-lg" />
                {t("BookNewCall_prospection_change_slot")}
            </button>

            <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                {t("GuestBooking_title")}
            </h2>
            <div className="mt-2 mb-0 text-sm text-gray-500 dark:text-lightBlue">
                {t("GuestBooking_subtitle")}
            </div>

            <form onSubmit={onSubmit} className="mt-4">
                <div className="flex gap-4">
                    <div className="w-full">
                        <label htmlFor="guestFirstName" className="label">
                            {t("GuestBooking_firstName")}
                        </label>
                        <input
                            type="text"
                            id="guestFirstName"
                            className="input"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div className="w-full">
                        <label htmlFor="guestLastName" className="label">
                            {t("GuestBooking_lastName")}
                        </label>
                        <input
                            type="text"
                            id="guestLastName"
                            className="input"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>
                <div className="w-full mt-4">
                    <label htmlFor="guestEmail" className="label">
                        {t("GuestBooking_email")}
                    </label>
                    <input
                        type="email"
                        id="guestEmail"
                        className="input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div className="w-full mt-4">
                    <label htmlFor="guestSubject" className="label">
                        {t("GuestBooking_subject")}
                    </label>
                    <textarea
                        id="guestSubject"
                        rows={3}
                        className="input-textarea"
                        placeholder={t("GuestBooking_subject_placeholder", {
                            name: mentorName,
                        })}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                    />
                </div>
                <div className="w-full mt-4">
                    <label className="label">
                        {t("GuestBooking_preference")}
                    </label>
                    <div className="mt-1.5">
                        <MeetingPreferenceSelector
                            value={meetingPreference}
                            onChange={setMeetingPreference}
                            phoneNumber={phoneNumber}
                            onPhoneChange={setPhoneNumber}
                        />
                    </div>
                </div>
                <ProgressButton
                    type="submit"
                    disabled={!isFormValid || submitting}
                    loading={submitting}
                    overlayClassName="flex items-center justify-center text-sm font-semibold py-3"
                    className="w-full mt-6 rounded-xl bg-primary btn-3d lip-orange text-white text-sm font-semibold py-3 hover:brightness-95 transition shadow-[0_6px_18px_-6px_rgba(232,81,29,0.55)] disabled:cursor-not-allowed disabled:shadow-none disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-white/10 dark:disabled:text-grayDark-500 flex items-center justify-center"
                >
                    {t("GuestBooking_submit")}
                </ProgressButton>
            </form>
        </>
    );
}

export default function GuestBookingForm(props) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
            scriptProps={{ async: true, defer: true }}
        >
            <GuestBookingFormInner {...props} />
        </GoogleReCaptchaProvider>
    );
}
